//
//  personnelRoleGuides.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module builds the guides shared by the personnel role pages.
//

import type { Permission } from "src/api/Tmd.dtos";

import {
    actionsStop,
    column,
    field,
    filterStop,
    grid,
    saveStop,
    type PageGuide,
    type PageGuideColumn,
    type PageGuideStop
} from "./pageGuide";

/**
 * Where a set of personnel roles is held. The kind decides which fields the role pages show and
 * which of them the page fills in for you.
 */
export type RoleOwnerKind =
    | "trial"
    | "site"
    | "supportingSite"
    | "institution"
    | "department"
    | "group";

/** Where a set of personnel roles is held. */
export type RoleOwner = {
    kind: RoleOwnerKind;
    /** Such as "this trial" or "this site". */
    owner: string;
    /** Example roles, such as "trial chair, trial coordinator". */
    examples: string;
    createPermission: Permission;
    updatePermission: Permission;
};

const personColumn = column(
    "Person",
    "The person's full name, with their username below when they can sign in."
);
const roleColumn = column(
    "Role",
    "The role held. The same person can appear more than once, once per role."
);
const contactColumn = column(
    "Contact",
    "The email and phone number to use for this person in this role, from their contact details for the institution the role is at."
);
const periodColumn = column(
    "Period",
    "When the role started and, once it has ended, when it ended. Hover to see how long it has been held; a role still running shows Ongoing."
);

/** The list columns of each kind of owner, in page order. */
const listColumns: Record<RoleOwnerKind, PageGuideColumn[]> = {
    trial: [
        roleColumn,
        personColumn,
        column(
            "Site",
            "The site the role is at, with the department below. Empty for trial-wide roles, such as the trial chair."
        ),
        contactColumn,
        periodColumn
    ],
    site: [
        roleColumn,
        personColumn,
        column(
            "Department",
            "The department of the site the person works in for this role, if one was chosen."
        ),
        contactColumn,
        periodColumn
    ],
    supportingSite: [
        roleColumn,
        personColumn,
        column(
            "Department",
            "The department of the supporting site the person works in for this role, if one was chosen."
        ),
        contactColumn,
        periodColumn
    ],
    institution: [
        roleColumn,
        personColumn,
        column(
            "Assignment",
            "The department the role is in; for a role at a supporting site, the institution it supports."
        ),
        contactColumn,
        periodColumn
    ],
    department: [roleColumn, personColumn, contactColumn, periodColumn],
    group: [roleColumn, personColumn, contactColumn, periodColumn]
};

/** The list of people with roles at an owner. */
export const roleListGuide = (roles: RoleOwner): PageGuide => ({
    intro: `The people with roles on ${roles.owner}, such as ${roles.examples}. A person's role decides what they can see and do on ${roles.owner}, which emails they receive, and any OpenClinica access they are given.`,
    stops: [
        filterStop(
            "Type a name, role, site or email to filter. Ended roles are hidden; switch on inactive records to include them."
        ),
        actionsStop(
            `Add gives a person a role on ${roles.owner}. The person must already exist, or can be added from the role form.`,
            roles.createPermission
        ),
        grid(
            "Select a row to change a role or end it. Ended roles are greyed out.",
            listColumns[roles.kind]
        )
    ],
    notes: [
        "Roles are ended rather than deleted, so the history of who held a role is kept.",
        "A person can hold several roles; their permissions combine."
    ]
});

/** The context fields a role page shows before the person, relative to the given prefix. */
const contextFields = (prefix: string, roles: RoleOwner): PageGuideStop[] => {
    if (roles.kind === "group") {
        return [
            field(
                `${prefix}groupId`,
                "Group",
                "The group the role is held in. It is set from the page you came from and cannot be changed here."
            )
        ];
    }

    const trial =
        roles.kind === "institution" || roles.kind === "department"
            ? field(
                  `${prefix}trialId`,
                  "Trial",
                  "Leave empty for a role at the institution itself, such as head of department. Choose a trial to make it a role at this institution for that trial, like a site investigator."
              )
            : field(
                  `${prefix}trialId`,
                  "Trial",
                  "The trial the role is on. It is set from the page you came from and cannot be changed here."
              );
    const institution = field(
        `${prefix}institutionId`,
        "Institution",
        roles.kind === "trial"
            ? "The site the person works at for this role. Leave empty for a trial-wide role such as trial chair; choose a site for site roles such as principal investigator."
            : "The institution the role is at. It is set from the page you came from and cannot be changed here."
    );
    const supportingSite = field(
        `${prefix}trialInstitutionSupportingSiteId`,
        "Supporting Site",
        roles.kind === "supportingSite"
            ? "The supporting site the role is at. It is set from the page you came from and cannot be changed here."
            : "Shown once a site is chosen. Choose a supporting site when the person works at a satellite centre of that site rather than the site itself."
    );

    return roles.kind === "trial" || roles.kind === "site" || roles.kind === "supportingSite"
        ? [trial, institution, supportingSite]
        : [trial, institution];
};

/** The role fields after the person, relative to the given prefix. */
const roleFields = (prefix: string, roles: RoleOwner, withEmail: boolean): PageGuideStop[] => [
    field(
        `${prefix}roleId`,
        "Role",
        `The role on ${roles.owner}. Its permissions apply to ${roles.owner} only, and it decides which emails the person receives there.`
    ),
    ...(roles.kind === "group"
        ? []
        : [
              field(
                  `${prefix}departmentId`,
                  "Department",
                  roles.kind === "department"
                      ? "The department the role is in. It is set from the page you came from and cannot be changed here."
                      : "The department of the institution the person works in for this role, if any. Add Department, beside the field, creates one that is missing."
              )
          ]),
    field(
        `${prefix}dateStarted`,
        "Date Started",
        "When the role began. It is shown in the Period column and used to report who held the role on a given date."
    ),
    ...(withEmail
        ? [
              field(
                  `${prefix}noEmailCommunication`,
                  "No Email Communication",
                  "Tick to stop emails that are sent to this role from reaching the person, without ending the role."
              )
          ]
        : []),
    field(
        `${prefix}active`,
        "Active",
        "Clear to end the role. The person loses the role's permissions, and Date Ceased appears so you can record when it ended."
    ),
    field(
        `${prefix}dateCeased`,
        "Date Ceased",
        "When the role ended. It appears once Active is cleared and is required then."
    )
];

const roleRules = [
    "Person, Role and Date Started are required; Date Ceased is required once the role is no longer active.",
    "A supporting site role needs a trial and an institution, and cannot also be a group role."
];

/** The page that gives a person a role. */
export const roleCreateGuide = (roles: RoleOwner): PageGuide => ({
    intro: `Give a person a role on ${roles.owner}. Choose the person, then the role and when it started.`,
    stops: [
        ...contextFields("personnelRole.", roles),
        field(
            "personnelRole.personnelId",
            "Person",
            "Search for the person by name or email. If they are not in the system yet, the add button beside the field creates them without leaving this page."
        ),
        ...roleFields("personnelRole.", roles, true),
        saveStop(
            "Submit adds the role; the person gains its permissions straight away. Submit And Return also goes back to the list.",
            roles.createPermission
        )
    ],
    rules: roleRules,
    notes: [
        "Roles mapped to OpenClinica create the person's OpenClinica access automatically; check the person's OpenClinica page if access does not appear."
    ]
});

/** The page that changes or ends a person's role. */
export const roleEditGuide = (roles: RoleOwner): PageGuide => ({
    intro: `One person's role on ${roles.owner}. Change its dates or email setting, or end it.`,
    stops: [
        ...contextFields("", roles),
        field(
            "personnelId",
            "Person",
            "Who holds the role. To give the role to someone else, end this role and add a new one, so the history stays accurate."
        ),
        ...roleFields("", roles, roles.kind !== "group"),
        saveStop(
            "Submit saves the role. Ending it removes the role's permissions from the person.",
            roles.updatePermission
        )
    ],
    rules: roleRules,
    notes: ["End roles rather than deleting them so the history of who held a role is kept."]
});
